import { Input, Form, Button, Space } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import * as _ from 'lodash';
import { useEffect } from 'react';
import SpinningWheel from '../../../../../../components/SpinningWheel';
import { usePrerenderUser } from '../../../../../../hooks/usePrerenderUser';
import { useEvent } from '../../../../../events/hooks/useEvent';
import { DOMAIN_REJECTION_MESSAGES, normalizeDomainInput } from '../../../../../../utils/normalizeDomainInput';

/**
 * Placeholder and third-party domains nobody can integrate. Not a shape rule —
 * `normalizeDomainInput` owns the shape — so it stays here rather than moving
 * into the shared helper, which the beta screen also uses and which must not
 * refuse a real customer's site.
 *
 * `localhost` is gone from the list because the helper answers first, and it
 * answers for `https://localhost` and `localhost:3000` too, which this list
 * never did.
 */
const BLACKLISTED_DOMAINS = [
  'example.com',
  'example.org',
  'example.net',
  'google.com',
  'facebook.com',
  'twitter.com',
  'test.com',
];

const EnterDomainForm = ({ onFinish, domain, disabled, error, isLoading }) => {
  const [form] = Form.useForm();
  const formDomain = Form.useWatch('domain', form);
  const { track } = useEvent();
  const prerenderUser = usePrerenderUser();

  useEffect(() => {
    const domainError = 'Entered domain is incorrect or not reachable. Please try again.';
    const trackingData = {
      domain_name: formDomain,
      subscription_plan: prerenderUser.chargebeePlanId,
    };

    if (error) {
      form.setFields([
        {
          name: 'domain',
          errors: [domainError],
        },
      ]);
      track('Integration Wizard: Domain Incorrect', {
        ...trackingData,
        error_message: domainError,
      });
    }
  }, [error, form]);

  useEffect(() => {
    if (domain) {
      form.setFieldsValue({ domain });
    }
  }, [domain, form]);

  const refuse = (message, trackingData) => {
    form.setFields([
      {
        name: 'domain',
        errors: [message],
      },
    ]);
    track('Integration Wizard: Domain Incorrect', {
      ...trackingData,
      error_message: message,
    });
  };

  /**
   * Validated through `normalizeDomainInput`, the one shared validator
   * (PRE-3289). It replaces the local shape rule, and it closes two holes the
   * local rules left: the blacklist compared the **raw** input with `includes`,
   * so `www.example.com` hid `example.com` from it, and `https://localhost`
   * carried a message about a URL rather than about a local address.
   *
   * The value handed to `onFinish` is still the host the customer typed. The
   * helper reduces to the root domain for the Cloudflare flow, which is keyed on
   * the apex; substituting that here would change **which host gets verified**
   * for every customer in the wizard, and a site served only from
   * `app.example.com` would be verified at `example.com`.
   */
  const onSubmitForm = (values) => {
    const trackingData = {
      domain_name: values.domain,
      subscription_plan: prerenderUser.chargebeePlanId,
    };

    const normalized = normalizeDomainInput(values.domain);

    if (!normalized.ok) {
      refuse(DOMAIN_REJECTION_MESSAGES[normalized.reason], trackingData);
      return;
    }

    if (BLACKLISTED_DOMAINS.includes(normalized.domain)) {
      refuse('This domain is not allowed. Please enter a different domain.', trackingData);
      return;
    }

    onFinish(values);
    track('Integration Wizard: Domain Entered', trackingData);
  };

  if (_.isNil(domain) && !error) {
    return <SpinningWheel />;
  }

  return (
    <Form form={form} onFinish={onSubmitForm} initialValues={domain} style={{ marginTop: 32 }}>
      <Space.Compact style={{ width: '100%', justifyContent: 'center' }}>
        <Form.Item
          style={{ width: '100%' }}
          name="domain"
          rules={[
            {
              required: true,
              message: 'Please enter your domain',
            },
          ]}
        >
          <Input placeholder="https://example.com" prefix={<GlobalOutlined />} autoFocus disabled={disabled} />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" disabled={disabled} loading={isLoading}>
            Verify Domain
          </Button>
        </Form.Item>
      </Space.Compact>
    </Form>
  );
};

export default EnterDomainForm;
